// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a `release.json` says about how far its values can be
 * trusted and where they came from (value error audit, 2026-10-19,
 * docs/AYNI_XR_VALUE_ERROR_AUDIT.md; the fields are the proposal in
 * docs/AYNI_XR_VALUE_FIDELITY_PLAN.md §2, sent to the stream host).
 *
 * Read from whichever of these a release carries, best first:
 * - `errorBudget` version 2 (codec error in codes, the codec setting,
 *   regridding against the native grid) and `source` (the custody of the
 *   source), as proposed;
 * - `errorBudget` version 1 (docs/AYNI_DATA_INTAKE_API.md §10.1), already
 *   read by `error-budget.ts`;
 * - the dataset builder's `provenance` (`codecFidelity`, `clippedFraction`,
 *   `regrid`, `source`), on every builder release since 2026-10;
 * - the stream host's `diagnostics.json` (`crf`, `lossless`), a separate
 *   file fetched after the release resolves (services/valueFidelity.ts).
 *
 * Pure, so `ReleaseEncoding` can carry it. A field a release leaves out is
 * null, never a guess.
 */

/** The codec's error, measured by decoding the published stream against the codes that went into the encoder. */
export interface CodecFidelity {
  readonly name: string | null
  /** True: lossless, so the codec adds nothing. Null: not stated. */
  readonly lossless: boolean | null
  readonly crf: number | null
  /** In codes, over every data pixel of every frame. */
  readonly codes: { readonly mean: number | null; readonly p99: number | null; readonly max: number | null; readonly fractionOverOne: number | null } | null
  /** Where the figures come from: the release's error budget, the builder's provenance, or the host's diagnostics. */
  readonly from: 'errorBudget' | 'builder' | 'diagnostics'
}

/** How the provider gives the data: values, discrete classes, or a coloured picture turned back into values. */
export type DistributedAs = 'values' | 'classes' | 'image'

/** The custody of the source, per release. */
export interface SourceCustody {
  readonly provider: string | null
  readonly organization: string | null
  readonly product: string | null
  readonly productId: string | null
  readonly doi: string | null
  readonly version: string | null
  readonly variable: string | null
  readonly distributedAs: DistributedAs | null
  /** For `classes`: the class edges, and what a pixel carries (midpoint, geometric-midpoint, lower-edge). */
  readonly classEdges: readonly number[] | null
  readonly classValue: string | null
  readonly access: string | null
  readonly licence: string | null
  readonly grid: { readonly crs: string | null; readonly resolution: string | null; readonly convention: string | null } | null
}

/** Regridding from the source grid to the frame. */
export interface RegridFidelity {
  readonly method: string | null
  /** The builder writes its method in words. */
  readonly text: string | null
  readonly placement: 'pixel-centre' | 'node' | null
  /** The frame against the provider's own values at native grid points, in the variable's units. */
  readonly vsNative: { readonly mean: number | null; readonly p99: number | null; readonly max: number | null } | null
}

/** One frame's entry in a frame manifest, as far as custody goes. */
export interface FrameCustody {
  readonly index: number
  readonly time: string | null
  readonly availability: string | null
  readonly pixelSha256: string | null
  readonly run: string | null
  readonly lead: string | null
  readonly validTime: string | null
  readonly files: readonly { readonly url: string; readonly sha256: string | null; readonly retrievedAt: string | null }[]
  readonly maxCodeError: number | null
  readonly clippedShare: number | null
  readonly exactSha256: string | null
}

/** Where a frame's entry can be found: inline (the host's tail segment) or in a manifest file. */
export interface FrameSegmentRef {
  readonly startFrame: number
  readonly frameCount: number
  readonly manifestUrl: string | null
  readonly inline: readonly FrameCustody[] | null
}

/** Everything a release says about its fidelity and custody. */
export interface ReleaseFidelity {
  readonly releaseUrl: string | null
  readonly releaseId: string | null
  readonly generation: number | null
  readonly publishedAt: string | null
  readonly codec: CodecFidelity | null
  readonly clipping: { readonly lowShare: number | null; readonly highShare: number | null; readonly furthest: number | null; readonly share: number | null } | null
  readonly regrid: RegridFidelity | null
  readonly source: SourceCustody | null
  /** Exact pre-codec codes per frame, fetched on demand (proposal §2.4). */
  readonly exactFrames: { readonly format: string; readonly template: string } | null
  /** Where each frame's custody entry is: the host's segments, or the builder's one manifest. */
  readonly frames: { readonly segments: readonly FrameSegmentRef[] } | { readonly manifestUrl: string } | null
  /** The host's `diagnostics.json`, read after the release resolves (crf, lossless). */
  readonly diagnosticsUrl: string | null
  /** For finding a frame by its time: the window's first instant, the cadence and the first frame's index (null when not stated). */
  readonly timing: { readonly startMs: number; readonly cadenceSeconds: number; readonly windowStartFrame: number } | null
}

const rec = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const str = (v: unknown, max = 300): string | null => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null)
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)

function urlFrom(raw: unknown, base: string | null): string | null {
  const u = typeof raw === 'string' ? raw : str(rec(raw)?.url, 2000)
  if (!u) return null
  try {
    return base ? new URL(u, base).toString() : new URL(u).toString()
  } catch {
    return null
  }
}

function codesOf(raw: unknown): CodecFidelity['codes'] {
  const c = rec(raw)
  if (!c) return null
  return { mean: num(c.mean), p99: num(c.p99), max: num(c.max), fractionOverOne: num(c.fractionOverOne) }
}

/** The codec part: error budget v2 first, then the builder's `codecFidelity`. */
function codecOf(r: Record<string, unknown>): CodecFidelity | null {
  const budget = rec(r.errorBudget)
  const c = rec(budget?.codec)
  if (c && (c.codes !== undefined || c.lossless !== undefined || c.crf !== undefined)) {
    return { name: str(c.name), lossless: bool(c.lossless), crf: num(c.crf), codes: codesOf(c.codes), from: 'errorBudget' }
  }
  const fid = rec(rec(r.provenance)?.codecFidelity)
  if (fid) {
    const max = num(fid.maxCodeError)
    return {
      name: str(fid.codec),
      lossless: bool(fid.lossless) ?? (max === 0 ? true : null),
      crf: num(fid.crf),
      codes: { mean: num(fid.meanCodeError), p99: null, max, fractionOverOne: num(fid.fractionOverOneCode) },
      from: 'builder',
    }
  }
  return null
}

function distributedAsOf(v: unknown): DistributedAs | null {
  return v === 'values' || v === 'classes' || v === 'image' ? v : null
}

/** The source custody: `source` (proposal) first, then the builder's `provenance.source`. */
function sourceOf(r: Record<string, unknown>): SourceCustody | null {
  const s = rec(r.source)
  if (s) {
    const grid = rec(s.grid)
    const edges = Array.isArray(s.classEdges) ? s.classEdges.filter((e): e is number => typeof e === 'number' && Number.isFinite(e)).slice(0, 64) : null
    return {
      provider: str(s.provider), organization: str(s.organization), product: str(s.product), productId: str(s.productId),
      doi: str(s.doi), version: str(s.version), variable: str(s.variable), distributedAs: distributedAsOf(s.distributedAs),
      classEdges: edges && edges.length ? edges : null, classValue: str(s.classValue), access: str(s.access), licence: str(s.licence),
      grid: grid ? { crs: str(grid.crs), resolution: str(grid.resolution), convention: str(grid.convention) } : null,
    }
  }
  const p = rec(r.provenance)
  const ps = rec(p?.source)
  if (ps) {
    return {
      provider: null, organization: null,
      product: str(ps.dataset) ?? str(ps.collection) ?? str(ps.product),
      productId: str(ps.dataset) ?? str(ps.collection),
      doi: str(ps.doi), version: str(ps.version), variable: str(ps.variable), distributedAs: null,
      classEdges: null, classValue: null, access: str(ps.access), licence: null, grid: null,
    }
  }
  return null
}

function regridOf(r: Record<string, unknown>): RegridFidelity | null {
  const rg = rec(rec(r.errorBudget)?.regrid)
  const text = str(rec(r.provenance)?.regrid, 400)
  if (!rg && !text) return null
  const vs = rec(rg?.vsNative)
  const placement = rg?.placement === 'pixel-centre' || rg?.placement === 'node' ? rg.placement : null
  return {
    method: str(rg?.method),
    text,
    placement,
    vsNative: vs ? { mean: num(vs.mean), p99: num(vs.p99), max: num(vs.max) } : null,
  }
}

function clippingOf(r: Record<string, unknown>): ReleaseFidelity['clipping'] {
  const c = rec(rec(r.errorBudget)?.clipping)
  const share = num(rec(r.provenance)?.clippedFraction)
  if (!c && share === null) return null
  return { lowShare: num(c?.lowShare), highShare: num(c?.highShare), furthest: num(c?.furthest), share }
}

/** One frame-manifest entry (host: `globalFrameIndex`; builder: `frameIndex`). */
export function parseFrameCustody(raw: unknown, base: string | null): FrameCustody | null {
  const f = rec(raw)
  if (!f) return null
  const index = num(f.globalFrameIndex) ?? num(f.frameIndex)
  if (index === null) return null
  // `sourceCustody` (the builder's `source` is already a string: "ssp245", "historical"); a `source` object is accepted too.
  const src = rec(f.sourceCustody) ?? rec(f.source)
  const fid = rec(f.fidelity)
  const files = Array.isArray(src?.files)
    ? src!.files.slice(0, 20).flatMap((x) => {
        const o = rec(x)
        const url = o ? urlFrom(o.url, base) : null
        return url ? [{ url, sha256: str(o!.sha256, 80), retrievedAt: str(o!.retrievedAt, 40) }] : []
      })
    : []
  return {
    index,
    time: str(f.scientificTime, 40),
    availability: str(f.availability, 40),
    pixelSha256: str(f.pixelSha256, 80),
    run: str(src?.run, 40),
    lead: str(src?.lead, 40),
    validTime: str(src?.validTime, 40),
    files,
    maxCodeError: num(fid?.maxCodeError) ?? num(f.maxCodeError),
    clippedShare: num(fid?.clippedShare),
    exactSha256: str(f.exactSha256, 80),
  }
}

function framesOf(r: Record<string, unknown>, base: string | null): ReleaseFidelity['frames'] {
  if (Array.isArray(r.segments) && r.segments.length && rec(r.segments[0])?.startFrame !== undefined) {
    const segments = r.segments.flatMap((raw) => {
      const s = rec(raw)
      const startFrame = num(s?.startFrame)
      const frameCount = num(s?.frameCount)
      if (!s || startFrame === null || frameCount === null) return []
      const inline = Array.isArray(s.frames) ? s.frames.flatMap((f) => parseFrameCustody(f, base) ?? []) : null
      return [{ startFrame, frameCount, manifestUrl: urlFrom(s.frameManifest, base), inline }]
    })
    return segments.length ? { segments } : null
  }
  const manifestUrl = urlFrom(r.frameManifest, base)
  return manifestUrl ? { manifestUrl } : null
}

function exactFramesOf(r: Record<string, unknown>): ReleaseFidelity['exactFrames'] {
  const e = rec(r.exactFrames)
  const format = str(e?.format, 40)
  const template = str(e?.template, 500)
  if (!format || !template || !template.includes('{frame}')) return null
  return { format, template }
}

function timingOf(r: Record<string, unknown>): ReleaseFidelity['timing'] {
  const start = Date.parse(str(rec(r.timeRange)?.start, 40) ?? str(r.availabilityStartTime, 40) ?? '')
  const cadence = num(r.timeCadenceSeconds)
  if (!Number.isFinite(start) || cadence === null || !(cadence > 0)) return null
  return { startMs: start, cadenceSeconds: cadence, windowStartFrame: num(r.windowStartFrame) ?? 0 }
}

/** What a release says about its fidelity and custody, or null when it says nothing usable. */
export function parseReleaseFidelity(release: unknown, releaseUrl: string | null = null): ReleaseFidelity | null {
  const r = rec(release)
  if (!r) return null
  const fidelity: ReleaseFidelity = {
    releaseUrl,
    releaseId: str(r.releaseId, 120),
    generation: num(r.generation),
    publishedAt: str(r.publishedAt, 40),
    codec: codecOf(r),
    clipping: clippingOf(r),
    regrid: regridOf(r),
    source: sourceOf(r),
    exactFrames: exactFramesOf(r),
    frames: framesOf(r, releaseUrl),
    diagnosticsUrl: urlFrom(r.diagnostics, releaseUrl),
    timing: timingOf(r),
  }
  return fidelity
}

/** The host's `diagnostics.json` as a codec record (it states the setting, not a measured error). */
export function codecFromDiagnostics(raw: unknown): CodecFidelity | null {
  const d = rec(raw)
  if (!d) return null
  const lossless = bool(d.lossless)
  const crf = num(d.crf)
  const name = str(d.codec, 60)
  if (lossless === null && crf === null) return null
  return {
    name,
    lossless,
    crf,
    codes: lossless ? { mean: 0, p99: 0, max: 0, fractionOverOne: 0 } : null,
    from: 'diagnostics',
  }
}
